<template>
  <v-container fluid>
    <v-progress-linear v-if="loading" indeterminate color="teal"/>
    <v-col v-for="challenge in challenges" :key="challenge.id">
      <v-card :color="getCardColor(challenge)" hover class="ma-5 mx-auto">
        <v-row>
          <v-col class="mr-auto pt-0 pb-3">
            <v-card-title>
              <div>{{ challenge.name }} ({{ challenge.owner.username }})
                <v-chip class="mr-2 mb-1" v-if="challenge.publishType==='PRIVATE'" text-color="white" color="deep-purple" small>Private</v-chip>
                <v-chip class="mr-2 mb-1" text-color="white" :color="getDifficultyColor(challenge)" small>{{ challenge.difficulty }}</v-chip>
                <v-chip class="mr-2 mb-1" v-if="challenge.timeOffset > 0" text-color="white" color="blue" small>MP +{{ challenge.timeOffset }}h</v-chip>
                <v-chip class="mr-2 mb-1" v-if="challenge.season !== undefined" text-color="white" color="teal darken-1" small>{{ challenge.season.name }}</v-chip>
                <v-icon class="mr-2 mb-1" v-if="challenge.challengeType === 'RACE' && challenge.inProgress" right>mdi-timer-sand</v-icon>
              </div>
            </v-card-title>
            <v-card-subtitle class="pb-0">
              <p class="mb-0">{{ getCardSubtitleLine1(challenge) }}</p>
              <p class="mb-0">{{ getCardSubtitleLine2(challenge) }}</p>

              <p v-if="showFinished" style="color: #30ac7c">{{ getFinishText(challenge) }}</p>
            </v-card-subtitle>
          </v-col>
          <v-col cols="auto">
            <v-card v-if="challenge.type === 'RACE'" rounded="lg" class="mx-5 mb-5 pa-5" min-width="250">
              <WeatherForecastWidget v-if="!showFinished" :date="getChallengeDate(challenge)"></WeatherForecastWidget>
              <WeatherForecastWidget v-else :challenge-id="challenge.id"></WeatherForecastWidget>
            </v-card>
          </v-col>
        </v-row>
        <v-card-text class="py-0">{{ challenge.description | truncate(255, '...') }}</v-card-text>
        <v-card-actions>
          <v-btn text color="teal accent-4" v-if="expandedChallengeId !== challenge.id" @click="switchCardDetails(challenge)">Show More</v-btn>
          <v-btn text color="teal accent-4" v-if="expandedChallengeId === challenge.id" @click="switchCardDetails(challenge)">Show Less</v-btn>
          <v-spacer></v-spacer>
          <v-btn text color="teal accent-4" v-if="challenge.publishType==='PRIVATE' && !challenge.approvalRequested" @click="requestChallengeApprovalDlg(challenge)">Publish</v-btn>
          <v-btn text color="teal accent-4" v-if="challenge.approvalRequested && !showApprovalRequests" disabled>Approval Pending</v-btn>
          <div v-if="showApprovalRequests && isAuthorized('ADMIN')">
            <v-btn text color="teal accent-4" @click="approveChallengeRequestDlg(challenge)">Approve</v-btn>
            <v-btn text color="teal accent-4" @click="denyChallengeRequestDlg(challenge)">Deny</v-btn>
          </div>
          <div v-if="isAuthorized('ADMIN') && challenge.type === 'RACE' && (challenge.inProgress || showFinished)">
            <v-btn text color="teal accent-4" @click="showChallengeEventLogs(challenge)">EventLog</v-btn>
          </div>
          <div v-if="showCreatorControls">
            <v-btn text color="teal accent-4" @click="editChallenge(challenge)">Edit</v-btn>
            <v-btn text color="teal accent-4" v-if="challenge.id !== challengeIdToDelete" @click="challengeIdToDelete=challenge.id">Delete</v-btn>
            <v-btn text color="orange" v-if="challengeIdToDelete === challenge.id" @click="deleteChallenge(challenge)">Confirm</v-btn>
          </div>
          <v-btn icon @click="switchCardDetails(challenge)">
            <v-icon>{{ expandedChallengeId === challenge.id ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
          </v-btn>
        </v-card-actions>
        <v-expand-transition>
          <div v-if="expandedChallengeId === challenge.id">
            <v-divider/>
            <ChallengeEditor v-if="showChallengeEditor" v-on:dialog-close="showChallengeEditor=false" :item="challenge"/>
            <v-container fluid>
              <v-row>
                <v-col style="min-width: 250px">
                  <v-container>
                    <v-row>
                      <v-switch class="mr-auto" label="Engine allowed" disabled v-model="challenge.allowEngine"></v-switch>
<!--                      <v-switch class="mr-auto" label="Autosail allowed" disabled v-model="challenge.allowAutoSail"></v-switch>-->
                    </v-row>
                    <v-row v-if="challenge.type === 'RACE' && !showFinished">
                      <v-text-field class="mr-auto" style="min-width: 80px" label="Participants" readonly :value="challengeLeaderboard.length + ' / ' + challenge.maximumBoats"></v-text-field>
                      <v-text-field label="Estimated Duration" prepend-icon="mdi-calendar" readonly :value="getDurationText(challenge)"></v-text-field>
                    </v-row>
                  </v-container>
                  <v-textarea label="Description" outlined readonly auto-grow :value="challenge.description" />
                </v-col>
                <v-col style="min-width: 250px" v-if="doShowCourse(challenge)">
                  <!-- padding-top: 100% preserves aspect ratio for height-->
                  <GMap :challenge="challenge"
                        :motion-infos="challengeUserPositions"
                        :show-refresh-btn="!showFinished"
                        :show-replay-controls="challenge.type==='RACE' && !challenge.inProgress && challengeUserPositions.length > 0"
                        v-on:refresh-required="updateLeaderboard(challenge)"
                        v-on:auto-refresh-required="autoUpdateLeaderboard(challenge)"
                        v-on:auto-refresh-cancelled="cancelAutoUpdateLeaderboard"
                        style="width: 100%; padding-top: 100%;"/>
                </v-col>
                <v-col v-if="challengeLeaderboard.length > 0" >
                  <v-card rounded="lg">
                    <v-card-title>Leaderboard</v-card-title>
                    <v-simple-table dense>
                      <template v-slot:default>
                        <thead>
                          <tr>
                            <th class="text-left">Rank</th>
                            <th class="text-left">Username</th>
                            <th class="text-left">Boat Name</th>
                            <th class="text-left">Time Taken</th>
                            <th class="text-left" v-if="challenge.type !== 'RACE'">Date</th>
                            <th class="text-left"></th>
                          </tr>
                        </thead>
                        <tbody>
                        <tr v-for="item in challengeLeaderboard" :key="item.id">
                          <td>{{ item.rank }}</td>
                          <td>{{ item.user.username }}</td>
                          <td>{{ item.boatName }}</td>
                          <td>{{ millisToMinutesAndSeconds(item.timeTaken) }}</td>
                          <td v-if="challenge.type !== 'RACE'">{{ item.date | formatDate }}</td>
                          <td v-if="item.user.country !== undefined" ><country-flag :country="item.user.country" /></td>
                        </tr>
                        </tbody>
                      </template>
                    </v-simple-table>
                  </v-card>
                </v-col>
              </v-row>
              <v-row justify="center" v-if="challenge.type === 'RACE'">
                <add-to-calendar v-if="!showFinished" :title="challenge.name" location="eSail World" :start="new Date(challenge.startTimeUtc)" :end="getEstimatedEndDate(challenge).toDate()" :details="challenge.description" inline-template>
                  <div>
                    <google-calendar id="google-calendar"><i class="fa fa-google"></i>Add to Google calendar</google-calendar>
                    <microsoft-calendar id="microsoft-calendar"><i class="fa fa-microsoft"></i> Add to Microsoft live calendar</microsoft-calendar>
                    <office365-calendar id="office365-calendar"><i class="fa fa-windows"></i> Add to Office365 outlook calendar</office365-calendar>
                  </div>
                </add-to-calendar>
              </v-row>
            </v-container>
          </div>
        </v-expand-transition>
      </v-card>
    </v-col>
    <v-dialog v-model="showApprovalRequestDialog" persistent max-width="480">
      <v-card>
        <v-card-title class="text-h5">Publish this challenge?</v-card-title>
        <v-card-text>You may publish this challenge for all eSail users. This will involve a manual review by one of the eSail Content Creators. Make sure that course and challenge are complete, descriptions are available and weather forecast fits the expected sailing conditions.</v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="green darken-1" text @click="showApprovalRequestDialog = false">Cancel</v-btn>
          <v-btn color="green darken-1" text @click="requestChallengeApproval(challengeToApprove)">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showApprovalResponseDialog" persistent max-width="480">
      <v-card>
        <v-card-title class="text-h5">Approve this challenge?</v-card-title>
        <v-card-text>Click "Confirm" to approve this request.  Make sure that course and challenge are complete, descriptions are available and weather forecast fits the reasonable sailing conditions. It is best practise to also try this challenge within eSail before approving. The challenge status will change to PUBLIC and all users will see this challenge in the public challenge feed.</v-card-text>
        <v-textarea class="mx-5" v-model="approvalResponseDialogMessage" solo autocomplete label="Message (optional)"/>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="green darken-1" text @click="showApprovalResponseDialog = false">Cancel</v-btn>
          <v-btn color="green darken-1" text @click="approveChallengeRequest(challengeToApprove)">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showDenialResponseDialog" persistent max-width="480">
      <v-card>
        <v-card-title class="text-h5">Reject this challenge?</v-card-title>
        <v-card-text>Click "Confirm" to deny this request. The challenge status will remain PRIVATE. Please add a message and describe why this request could not be accepted.</v-card-text>
        <v-textarea class="mx-5" v-model="approvalResponseDialogMessage" solo autocomplete label="Message" :rules="[v => v.length >= 10 || 'Min 10 characters']"/>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="green darken-1" text @click="showDenialResponseDialog = false">Cancel</v-btn>
          <v-btn color="green darken-1" text @click="denyChallengeRequest(challengeToApprove)">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-dialog v-model="showEventLog" persistent max-width="90%">
      <v-card>
        <v-card-title class="text-h5">Challenge Event Log</v-card-title>
        <v-text-field class="ma-3" v-model="eventLogSearch" append-icon="mdi-magnify" label="Search" single-line hide-details/>
        <v-data-table dense :headers="eventLogHeaders" :search="eventLogSearch" :items="eventLogs" :items-per-page=10000 hide-default-footer>
          <template v-slot:item.timestamp="{ item }">
            <div>{{ item.timestamp | formatDateTime }}</div>
          </template>
<!--          <template v-slot:item.logType="{ item }">-->
<!--            <v-chip :color="getLogTypeColor(item.logType)" dark>{{ item.logType }}</v-chip>-->
<!--          </template>-->
        </v-data-table>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="green darken-1" text @click="showEventLog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>

</template>

<script>

import ChallengeEditor from "@/components/admin/ChallengeEditor";
import _ from 'lodash';
import Vue from "vue";
import moment from "moment";
import {eSailServerAuth, eSailServerPublic} from "@/plugins/axios";
import GMap from './GMap';
import WeatherForecastWidget from "./WeatherForecastWidget";
import AuthMixin from "../mixins/AuthMixin"

export default {
  name: "ChallengeList",
  props: {
    challengeType: null,
    selectedUser: null,
    showFinished: { type: Boolean, default: false },
    isPublicRaceHistory: { type: Boolean, default: false },
    showApprovalRequests: { type: Boolean, default: false },
    showCreatorControls: { type: Boolean, default: false }
  },
  components: {
    ChallengeEditor,
    GMap,
    WeatherForecastWidget
  },
  mixins: [
    AuthMixin
  ],
  data: () => ({
    loading: false,
    showApprovalRequestDialog: false,
    showApprovalResponseDialog: false,
    showDenialResponseDialog: false,
    approvalResponseDialogMessage: "",
    challengeToApprove: null,
    expandedChallengeId: 0,
    challengeIdToDelete: 0,
    challenges: null,
    challengeLeaderboard: [],
    challengeUserPositions: [],
    autoUpdateTimer: null,
    showChallengeEditor: false,
    showEventLog: false,
    eventLogSearch: null,
    eventLogHeaders: [
      { text: 'Timestamp', value: 'timestamp' },
      { text: 'Type', value: 'logType' },
      { text: 'User', value: 'user.username' },
      { text: 'Boat', value: 'user.boatName' },
      { text: 'Message', value: 'message' }
    ],
    eventLogs: [],
  }),
  watch: {
    challengeType: {
      immediate: true,
      handler () {
        this.updateChallengeList()
      }
    },
    selectedUser: {
      immediate: true,
      handler () {
        if (this.selectedUser && this.selectedUser.id) {
          this.updateChallengeList()
        }
      }
    }
  },
  computed: {
    isMobile() {
      return this.$vuetify.breakpoint.mobile
    }
  },
  beforeDestroy() {
    clearInterval(this.autoUpdateTimer)
  },
  mounted() {
    //not required because method is called upon setting challenge type to query
    // this.updateChallengeList()
  },
  methods: {
    updateChallengeList() {
      if (this.loading) return
      if (this.showFinished) this.loading = true
      const user = (this.selectedUser && this.selectedUser.id) ? this.selectedUser.id : "current"
      let promise = null
      if (this.showApprovalRequests) {
        const params = new URLSearchParams([['approval-required', true]])
        promise = eSailServerAuth.get('challenges', {
          headers: {Authorization: `Bearer ` + this.$auth.token()},
          params
        })
      } else if (this.showFinished) {
        if (this.isPublicRaceHistory) {
          promise = eSailServerPublic.get('info/game/challenges/race-history')
        } else {
          let params = new URLSearchParams([['type', this.challengeType], ['finished', true], ['sortAsc', 'false']])
          if (this.isPublicRaceHistory) params.append('size', '10')
          promise = eSailServerAuth.get('users/' + user + '/challenges/participations', {
            headers: {Authorization: `Bearer ` + this.$auth.token()},
            params
          })
        }
      } else {
        const params = new URLSearchParams([['type', this.challengeType], ['include-private-challenges', true]])
        if (this.challengeType === 'TIME_TRIAL') params.append('sortBy', 'name')
        promise = this.$auth.user() ? eSailServerAuth.get('challenges', {
              headers: {Authorization: `Bearer ` + this.$auth.token()},
              params
            }) :
            eSailServerPublic.get('info/game/challenges', {params})
      }
      promise.then(response => {
        this.challenges = response.data
        _.each(this.challenges, challenge => {
          if (challenge.description) {
            challenge.description += "\n\n" + challenge.course.description
          } else {
            challenge.description = challenge.course.description
          }
        })
        if ((this.showApprovalRequests && this.challenges.length === 0) ||
           (this.challengeType === 'RACE' && (this.challenges === null || this.challenges.length === 0))) {
          this.$emit("noItems")
        }
        this.loading = false
      })
    },
    switchCardDetails(challenge) {
      this.expandedChallengeId = this.expandedChallengeId === challenge.id ? 0 : challenge.id
      if (this.expandedChallengeId !== 0) {
        this.challengeLeaderboard = null
        this.updateLeaderboard(challenge)
      }
    },
    doShowCourse(challenge) {
      return challenge.course.type === 'RACE' || challenge.showCourseOnMap || this.$auth.user() && challenge.owner.id === this.$auth.user().id || this.showApprovalRequests || this.showFinished
    },
    updateLeaderboard(challenge) {
      //cancel timer when challenge details are collapsed
      if (this.autoUpdateTimer && (this.expandedChallengeId === 0 || this.expandedChallengeId !== challenge.id)) {
        this.cancelAutoUpdateLeaderboard()
        return
      }
      //always update inProgress status (hack: based on local browser time in order to avoid reloading complete challenge from server)
      challenge.inProgress = moment(challenge.startTimeUtc).isBefore(moment()) && !challenge.finishTimeUtc

      //get leaderboard for current challenge
      if (this.showFinished || challenge.inProgress || challenge.type === 'TIME_TRIAL') {
        eSailServerPublic.get('info/game/challenges/' + challenge.id + '/leaderboard?size=10')
          .then(response => {
                this.challengeLeaderboard = challenge.type === 'RACE' ? response.data : _.filter(response.data, item => {
                  return item.disqualified === false && item.rank !== this.$MAX_INT
                })
                _.each(this.challengeLeaderboard, item => {
                  if (item.rank === this.$MAX_INT) {
                    item.rank = "Disqualified"
                    item.timeTaken = item.message
                  }
                  if (!item.boatName) {
                    item.boatName = item.user.boatName
                  }
                })
              }
          )
      }
      //add remaining challenge users by motion info
      if (challenge.type === 'RACE') this.updateGMapAvatars()
      this.challengeLeaderboard = _.sortBy(this.challengeLeaderboard, 'rank')
    },
    autoUpdateLeaderboard(challenge) {
      this.autoUpdateTimer = setInterval(() => this.updateLeaderboard(challenge), 30000);
    },
    cancelAutoUpdateLeaderboard() {
      if (this.autoUpdateTimer) {
        clearInterval(this.autoUpdateTimer)
        this.autoUpdateTimer = null
      }
    },
    updateGMapAvatars() {
      eSailServerPublic.get('info/game/challenges/' + this.expandedChallengeId + '/participants/positions')
        .then(response => {
            this.challengeUserPositions = response.data
            if (this.challengeUserPositions.length > 0) {
              const transformedLeaderboardItems = _.map(this.challengeUserPositions, item => {
                return {
                  user: item.user,
                  boatName: item.boatName,
                  rank: "in progress",
                  timeTaken: "N/A"
                }
              })
              const leaderboardUserIds = _.map(this.challengeLeaderboard, item => { return item.user.id })
              _.each(transformedLeaderboardItems, item => {
                if (!leaderboardUserIds.includes(item.user.id)) {
                  this.challengeLeaderboard.push(item)
                }
              })
            }
            _.remove(this.challengeLeaderboard, item => {
              return item.rank === "in progress" && _.find(this.challengeUserPositions, { user: { id: item.user.id }}, 0) === undefined
            })
          }
        )
    },
    getFinishText(challenge) {
      if (this.isPublicRaceHistory) {
        return "Finished " + Vue.filter('formatDateTime')(challenge.finishTimeUtc)
      } else {
        let lbItem = challenge.ownLeaderboardItems.challengeResult
        let rank = (lbItem.disqualified === true) ? "disqualified" : '' + lbItem.rank
        return "Finished " + Vue.filter('formatDateTime')(lbItem.date) + ", Rank: " + rank
      }
    },
    getCardSubtitleLine1(challenge) {
      let text = "Distance " + challenge.course.lengthNm.toFixed(2) + "nm"
      if (this.isAuthorized('ADMIN'))  text += " - ID: " + challenge.id
      if (challenge.type === 'RACE') {
        return "Local Start Time " + Vue.filter('formatDateTime')(challenge.startTimeUtc) + " - " + text
      } else {
        return text
      }
    },
    getCardSubtitleLine2(challenge) {
      let text = challenge.publishType === 'PRIVATE' ? "Access-Code: " + challenge.accessCode : ""
      if (this.showApprovalRequests) text += ", Type: " + challenge.type
      return text
    },
    getDurationText(challenge) {
      const duration = this.getEstimatedEndDate(challenge).diff(challenge.startTimeUtc)
      return this.millisToMinutesAndSeconds(duration)
    },
    getDifficultyColor(challenge) {
      switch(challenge.difficulty) {
        case 'EASY':
          return 'green';
        case 'MEDIUM':
          return 'orange'
        case 'HARD':
          return 'red'
      }
    },
    getEstimatedEndDate(challenge) {
      const courseLength = challenge.course.lengthNm
      let requiredHours = Number.parseFloat(courseLength) / 5   //approximated time when sailing with 5knots
      let endDate = moment(challenge.startTimeUtc).add(requiredHours, 'hours')
      //cut minutes & seconds and add 30 minutes buffer
      endDate = endDate.minute() > 30 ? endDate.minute(30).second(0) : endDate.minute(0).second(0)
      return endDate.add(30, 'minutes')
    },
    millisToMinutesAndSeconds(millis) {
      if (isNaN(millis)) return millis
      const duration = moment.duration(millis)
      let res =  this.to2Digits(duration.minutes()) + "m:" + this.to2Digits(duration.seconds()) + "s"
      return duration.hours() > 0 ? this.to2Digits(duration.hours()) + "h:" + res : res
    },
    to2Digits(number) {
      return number < 10 ? "0" + number : number
    },
    getChallengeDate(challenge) {
      return moment(challenge.startTimeUtc).add(challenge.timeOffset, 'hours')
    },
    getCardColor(challenge) {
      return (challenge.challengeType === 'RACE' && (this.showFinished || challenge.inProgress)) ? "yellow lighten-5" : "grey lighten-5"
    },
    requestChallengeApprovalDlg(challenge) {
      this.challengeToApprove = challenge
      this.showApprovalRequestDialog = true
    },
    requestChallengeApproval(challenge) {
      this.showApprovalRequestDialog = false;
      eSailServerAuth.post('challenges/' + challenge.id + "/approval/request", null, { headers: { Authorization: `Bearer ` + this.$auth.token() }})
          .then(() => { this.updateChallengeList() })
    },
    approveChallengeRequestDlg(challenge) {
      this.challengeToApprove = challenge
      this.approvalResponseDialogMessage = ""
      this.showApprovalResponseDialog = true
    },
    approveChallengeRequest(challenge) {
      this.showApprovalResponseDialog = false;
      eSailServerAuth.post('challenges/' + challenge.id + "/approval", this.approvalResponseDialogMessage, { headers: { Authorization: `Bearer ` + this.$auth.token() }})
          .then(() => { this.updateChallengeList() })
    },
    denyChallengeRequestDlg(challenge) {
      this.challengeToApprove = challenge
      this.approvalResponseDialogMessage = ""
      this.showDenialResponseDialog = true
    },
    denyChallengeRequest(challenge) {
      this.showDenialResponseDialog = false;
      eSailServerAuth.delete('challenges/' + challenge.id + "/approval", { data: this.approvalResponseDialogMessage, headers: { Authorization: `Bearer ` + this.$auth.token() }})
          .then(() => { this.updateChallengeList() })
    },
    editChallenge(challenge) {
      this.expandedChallengeId = challenge.id
      this.showChallengeEditor = true
    },
    deleteChallenge(challenge) {
      this.challengeIdToDelete = 0
      eSailServerAuth.delete('admin/challenges/' + challenge.id, { headers: { Authorization: `Bearer ` + this.$auth.token() }})
          .then(() => { this.updateChallengeList() })
    },
    showChallengeEventLogs(challenge) {
      eSailServerAuth.get('admin/challenges/' + challenge.id + "/eventlog", { headers: { Authorization: `Bearer ` + this.$auth.token() }})
          .then(response => { this.eventLogs = response.data })
      this.showEventLog = true
    },
    getLogTypeColor(logType) {
      switch (logType) {
        case 'PARTICIPATION':
          return 'blue'
        case 'LEADERBOARD':
          return 'green'
        default:
          return 'gray'
      }
    }
  }
}
</script>

<style scoped>

/* see https://github.com/vuetifyjs/vuetify/issues/9130 */
.v-card__text, .v-card__title {
  word-break: normal; /* maybe !important  */
}
</style>